/** @format */

import { Edge, Node } from "react-flow-renderer";
import {
  TOrgPerson,
  TOrgStructure,
} from "../../../../redux-modules/declarations/maps/organigramma";

// ---- Costanti di layout (NODE_WIDTH e l'altezza minima della card coincidono con il CSS) ----
export const NODE_WIDTH = 240;
/** altezza minima della card: quelle con nome o ruolo su più righe sono più alte */
export const NODE_HEIGHT = 64;

const SIBLING_GAP = 48; // spazio orizzontale tra sottoalberi fratelli
const ROOT_TREE_GAP = 120; // spazio orizzontale tra alberi radice
const BASE_LEVEL_HEIGHT = 150; // distanza verticale (top -> top) tra padre e figli con card di altezza minima
const BRANCH_HALF = (BASE_LEVEL_HEIGHT - NODE_HEIGHT) / 2; // metà dello spazio tra padre e figli
const STAFF_STUB = 40; // tratto orizzontale tra il tronco e il lato destro del nodo staff
const STAFF_COLUMN_WIDTH = NODE_WIDTH / 2 + STAFF_STUB; // spazio riservato a sinistra per lo staff
const STAFF_FIRST_GAP = 16; // spazio tra il fondo del responsabile e il primo nodo staff
const STAFF_GAP = 12; // spazio tra nodi staff impilati
const BEND_MARGIN = 26; // distanza tra l'ultimo staff e il ramo orizzontale
const CHILD_MARGIN = 44; // distanza tra il ramo orizzontale e i figli

const GROUP_PADDING_X = 20;
const GROUP_PADDING_Y = 20;
const GROUP_HEADER_HEIGHT = 22;
/** quanto un riquadro sporge sopra la prima card che contiene (padding + etichetta) */
const GROUP_TOP_INSET = GROUP_PADDING_Y + GROUP_HEADER_HEIGHT;
/** distanza minima tra un ramo orizzontale e il bordo superiore del riquadro dei figli */
const BRANCH_CLEARANCE = 22;

export const PERSON_NODE = "orgPerson";
export const GROUP_NODE = "orgGroup";
export const ORG_EDGE = "orgEdge";

export interface TRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TPersonNodeData {
  person: TOrgPerson;
  /** altezza della card decisa dal layout (min-height della card) */
  height: number;
  /** collaboratori diretti (linea + staff) */
  directCount: number;
  /** persone nascoste quando il ramo è compresso */
  hiddenCount: number;
  collapsed: boolean;
  highlighted?: boolean;
  dimmed?: boolean;
  focused?: boolean;
}

export interface TGroupNodeData {
  structure: Pick<TOrgStructure, "id" | "name" | "background_color" | "text_color">;
  depth: number;
  dimmed?: boolean;
}

export interface TOrgEdgeData {
  isStaff?: boolean;
  bendOffset?: number;
  dimmed?: boolean;
}

export interface TCardPosition {
  x: number;
  y: number;
  height: number;
}

export interface TOrgLayout {
  nodes: Node[];
  edges: Edge<TOrgEdgeData>[];
  /** posizione (angolo in alto a sinistra) e altezza di ogni persona visibile, per id stringa */
  positions: Map<string, TCardPosition>;
  bounds: TRect;
}

// ---------------------------------------------------------------------------
// Indice dell'albero
// ---------------------------------------------------------------------------

export interface TOrgIndexEntry {
  person: TOrgPerson;
  parentId: number | null;
  depth: number;
  descendants: number;
}

export const buildOrgIndex = (tree: TOrgPerson[]): Map<number, TOrgIndexEntry> => {
  const index = new Map<number, TOrgIndexEntry>();

  const visit = (person: TOrgPerson, parentId: number | null, depth: number): number => {
    const entry: TOrgIndexEntry = { person, parentId, depth, descendants: 0 };
    index.set(person.id, entry);
    let total = 0;
    (person.listaDipendenti || []).forEach(child => {
      total += 1 + visit(child, person.id, depth + 1);
    });
    entry.descendants = total;
    return total;
  };

  tree.forEach(root => visit(root, null, 0));
  return index;
};

/** Id della persona e di tutti i suoi collaboratori (diretti e indiretti). */
export const subtreeIdsOf = (index: Map<number, TOrgIndexEntry>, personId: number): Set<number> => {
  const ids = new Set<number>();
  const entry = index.get(personId);
  if (!entry) return ids;

  const stack: TOrgPerson[] = [entry.person];
  while (stack.length) {
    const current = stack.pop() as TOrgPerson;
    ids.add(current.id);
    (current.listaDipendenti || []).forEach(child => stack.push(child));
  }
  return ids;
};

/** Antenati (dal responsabile diretto fino alla radice). */
export const ancestorIdsOf = (index: Map<number, TOrgIndexEntry>, personId: number): number[] => {
  const ids: number[] = [];
  let current = index.get(personId)?.parentId ?? null;
  while (current !== null && !ids.includes(current)) {
    ids.push(current);
    current = index.get(current)?.parentId ?? null;
  }
  return ids;
};

// ---------------------------------------------------------------------------
// PASSATA 1 (bottom-up): larghezze e altezze dei sottoalberi
// ---------------------------------------------------------------------------

interface TMetrics {
  width: number;
  staffWidth: number;
  line: TOrgPerson[];
  staff: TOrgPerson[];
  levelHeight: number;
  bendOffset: number;
  /** y di ogni nodo staff, relativa al top del responsabile */
  staffOffsets: number[];
  /** spazio orizzontale tra ogni figlio di linea e il successivo */
  lineGaps: number[];
  /** livelli di riquadri aperti dentro il sottoalbero (esclusa la persona stessa) */
  innerNesting: number;
}

/** persona -> id del riquadro di struttura che la contiene (vuota se le strutture sono nascoste) */
type TGroupOf = Map<number, string>;

interface TMetricsContext {
  collapsed: Set<number>;
  metrics: Map<number, TMetrics>;
  groupOf: TGroupOf;
  /** altezza di ogni card visibile */
  heights: Map<number, number>;
}

const heightOf = (heights: Map<number, number>, personId: number): number =>
  heights.get(personId) ?? NODE_HEIGHT;

const visibleChildren = (person: TOrgPerson, collapsed: Set<number>): TOrgPerson[] =>
  collapsed.has(person.id) ? [] : person.listaDipendenti || [];

/**
 * Altezza di ogni card visibile a partire da quella misurata sul testo.
 * I collaboratori di linea dello stesso responsabile stanno sulla stessa riga e
 * prendono tutti l'altezza del più alto, così la riga resta ordinata.
 */
const assignCardHeights = (
  tree: TOrgPerson[],
  collapsed: Set<number>,
  measured: Map<number, number>,
): Map<number, number> => {
  const heights = new Map<number, number>();
  const own = (p: TOrgPerson): number => Math.max(NODE_HEIGHT, measured.get(p.id) ?? NODE_HEIGHT);

  const visit = (person: TOrgPerson): void => {
    const children = visibleChildren(person, collapsed);
    const line = children.filter(c => !c.is_staff);
    const rowHeight = line.reduce((max, c) => Math.max(max, own(c)), NODE_HEIGHT);
    children.forEach(c => {
      heights.set(c.id, c.is_staff ? own(c) : rowHeight);
      if (!c.is_staff) visit(c);
    });
  };

  tree.forEach(root => {
    heights.set(root.id, own(root));
    visit(root);
  });
  return heights;
};

/** true se `person` apre un riquadro diverso da quello del responsabile (null = radice). */
const opensGroup = (person: TOrgPerson, parentId: number | null, groupOf: TGroupOf): boolean => {
  const group = groupOf.get(person.id);
  return group !== undefined && (parentId === null || group !== groupOf.get(parentId));
};

/**
 * Spazio orizzontale tra due sottoalberi adiacenti: oltre al gap di base, lascia posto
 * al padding laterale di ogni riquadro che si apre dentro i due sottoalberi, così i
 * riquadri annidati non invadono quelli dei fratelli. Se i due fratelli aprono lo
 * stesso riquadro, quel livello è condiviso e non serve spazio aggiuntivo.
 */
const siblingGap = (
  a: TOrgPerson,
  b: TOrgPerson,
  parentId: number | null,
  baseGap: number,
  { metrics, groupOf }: TMetricsContext,
): number => {
  const opensA = opensGroup(a, parentId, groupOf);
  const opensB = opensGroup(b, parentId, groupOf);
  let levels =
    (opensA ? 1 : 0) +
    (metrics.get(a.id) as TMetrics).innerNesting +
    (opensB ? 1 : 0) +
    (metrics.get(b.id) as TMetrics).innerNesting;
  if (opensA && opensB && groupOf.get(a.id) === groupOf.get(b.id)) levels -= 2;
  return baseGap + levels * GROUP_PADDING_X;
};

const computeMetrics = (person: TOrgPerson, mctx: TMetricsContext): number => {
  const { collapsed, metrics, groupOf, heights } = mctx;
  const cardHeight = heightOf(heights, person.id);
  const children = visibleChildren(person, collapsed);
  const staff = children.filter(c => c.is_staff);
  const line = children.filter(c => !c.is_staff);
  const opens = (child: TOrgPerson): boolean => opensGroup(child, person.id, groupOf);

  const staffWidth = staff.length > 0 ? STAFF_COLUMN_WIDTH : 0;

  // ---- figli di linea: larghezze e spazi tra fratelli
  let lineWidth = 0;
  const lineGaps: number[] = [];
  if (line.length > 0) {
    line.forEach(c => {
      lineWidth += computeMetrics(c, mctx);
    });
    for (let i = 0; i < line.length - 1; i++) {
      const gap = siblingGap(line[i], line[i + 1], person.id, SIBLING_GAP, mctx);
      lineGaps.push(gap);
      lineWidth += gap;
    }
  }

  const mainWidth = Math.max(NODE_WIDTH, lineWidth);

  // ---- staff impilati: se uno staff apre un riquadro, lascia posto a padding ed etichetta
  const staffOffsets: number[] = [];
  let staffBottom = cardHeight;
  staff.forEach((s, i) => {
    let gap: number;
    if (i === 0) {
      gap = STAFF_FIRST_GAP + (opens(s) ? GROUP_TOP_INSET : 0);
    } else {
      const prev = staff[i - 1];
      gap = STAFF_GAP;
      if (groupOf.get(prev.id) !== groupOf.get(s.id)) {
        gap += (opens(prev) ? GROUP_PADDING_Y : 0) + (opens(s) ? GROUP_TOP_INSET : 0);
      }
    }
    const top = staffBottom + gap;
    staffOffsets.push(top);
    staffBottom = top + heightOf(heights, s.id);
  });

  // ---- posizione del ramo orizzontale e altezza del livello
  // Se un figlio di linea apre un riquadro, il ramo deve restare sopra il bordo di quel
  // riquadro (non sul perimetro): i figli scendono di padding + etichetta + margine.
  const childMargin = (base: number): number =>
    line.some(opens) ? Math.max(base, GROUP_TOP_INSET + BRANCH_CLEARANCE) : base;

  let levelHeight: number;
  let bendOffset: number;
  if (staff.length > 0) {
    // Il ramo passa sotto l'ultimo staff; se lo staff sta in un riquadro che finisce lì
    // (non prosegue verso i figli di linea) il ramo deve scavalcarne anche il padding.
    const last = staff[staff.length - 1];
    const lastGroup = groupOf.get(last.id);
    const boxContinues =
      lastGroup !== undefined &&
      lastGroup === groupOf.get(person.id) &&
      line.some(c => groupOf.has(c.id));
    const staffBoxBottom = lastGroup !== undefined && !boxContinues ? GROUP_PADDING_Y : 0;
    const bendY = staffBottom + staffBoxBottom + BEND_MARGIN;
    levelHeight = Math.max(cardHeight + 2 * BRANCH_HALF, bendY + childMargin(CHILD_MARGIN));
    bendOffset = bendY - cardHeight;
  } else {
    bendOffset = BRANCH_HALF;
    levelHeight = cardHeight + bendOffset + childMargin(bendOffset);
  }

  let innerNesting = staff.some(opens) ? 1 : 0;
  line.forEach(c => {
    innerNesting = Math.max(innerNesting, (opens(c) ? 1 : 0) + (metrics.get(c.id) as TMetrics).innerNesting);
  });

  const width = mainWidth + staffWidth;
  metrics.set(person.id, {
    width,
    staffWidth,
    line,
    staff,
    levelHeight,
    bendOffset,
    staffOffsets,
    lineGaps,
    innerNesting,
  });
  return width;
};

// ---------------------------------------------------------------------------
// PASSATA 2 (top-down): coordinate assolute
// ---------------------------------------------------------------------------

interface TLayoutContext {
  collapsed: Set<number>;
  metrics: Map<number, TMetrics>;
  index: Map<number, TOrgIndexEntry>;
  heights: Map<number, number>;
  personNodes: Node<TPersonNodeData>[];
  edges: Edge<TOrgEdgeData>[];
  positions: Map<string, TCardPosition>;
}

const personNode = (
  person: TOrgPerson,
  x: number,
  y: number,
  ctx: TLayoutContext,
): Node<TPersonNodeData> => {
  const collapsed = ctx.collapsed.has(person.id);
  const directCount = (person.listaDipendenti || []).length;
  const height = heightOf(ctx.heights, person.id);
  ctx.positions.set(String(person.id), { x, y, height });

  return {
    id: String(person.id),
    type: PERSON_NODE,
    position: { x, y },
    data: {
      person,
      height,
      directCount,
      hiddenCount: collapsed ? ctx.index.get(person.id)?.descendants ?? 0 : 0,
      collapsed,
    },
    // React Flow disattiva gli eventi sui nodi non selezionabili/trascinabili:
    // le card devono invece ricevere clic, menu e drop.
    style: { pointerEvents: "all" },
    draggable: false,
    selectable: false,
    connectable: false,
  };
};

const assignPositions = (
  person: TOrgPerson,
  leftX: number,
  y: number,
  ctx: TLayoutContext,
): number => {
  const m = ctx.metrics.get(person.id) as TMetrics;
  const nodeId = String(person.id);
  const childrenLeft = leftX + m.staffWidth;
  const childrenWidth = m.width - m.staffWidth;
  const nextY = y + m.levelHeight;

  let centerX: number;
  if (m.line.length > 0) {
    let cursor = childrenLeft;
    const centers: number[] = [];

    m.line.forEach((child, i) => {
      ctx.edges.push({
        id: `e-${nodeId}-${child.id}`,
        source: nodeId,
        target: String(child.id),
        type: ORG_EDGE,
        data: { bendOffset: m.bendOffset },
      });
      centers.push(assignPositions(child, cursor, nextY, ctx));
      cursor += (ctx.metrics.get(child.id) as TMetrics).width + (m.lineGaps[i] ?? 0);
    });

    centerX = (centers[0] + centers[centers.length - 1]) / 2;
  } else {
    centerX = childrenLeft + childrenWidth / 2;
  }

  m.staff.forEach((staffPerson, i) => {
    ctx.edges.push({
      id: `e-staff-${nodeId}-${staffPerson.id}`,
      source: nodeId,
      target: String(staffPerson.id),
      type: ORG_EDGE,
      data: { isStaff: true },
    });
    ctx.personNodes.push(
      personNode(
        staffPerson,
        centerX - STAFF_STUB - NODE_WIDTH,
        y + m.staffOffsets[i],
        ctx,
      ),
    );
  });

  ctx.personNodes.push(personNode(person, centerX - NODE_WIDTH / 2, y, ctx));
  return centerX;
};

// ---------------------------------------------------------------------------
// Riquadri delle strutture (annidati come nell'originale)
// ---------------------------------------------------------------------------
// Finché la struttura di un collaboratore coincide con quella del responsabile
// resta nello stesso riquadro; appena cambia si apre un nuovo riquadro, annidato
// in quello del responsabile. Collaboratori dello stesso responsabile con la
// stessa struttura finiscono nello stesso riquadro.

interface TGroup {
  id: string;
  structureId: number;
  parentGroupId: string | null;
  depth: number;
  memberIds: string[];
  childGroupIds: string[];
}

const buildGroups = (
  tree: TOrgPerson[],
  collapsed: Set<number>,
  structureById: Map<number, TOrgStructure>,
): Map<string, TGroup> => {
  const groups = new Map<string, TGroup>();
  const byContext = new Map<string, TGroup>();
  let counter = 0;

  const ensureGroup = (structureId: number, parent: TGroup | null): TGroup => {
    const key = `${parent ? parent.id : "root"}::${structureId}`;
    const existing = byContext.get(key);
    if (existing) return existing;

    const group: TGroup = {
      id: `group-${structureId}-${counter++}`,
      structureId,
      parentGroupId: parent ? parent.id : null,
      depth: parent ? parent.depth + 1 : 0,
      memberIds: [],
      childGroupIds: [],
    };
    groups.set(group.id, group);
    byContext.set(key, group);
    if (parent) parent.childGroupIds.push(group.id);
    return group;
  };

  const visit = (person: TOrgPerson, parent: TGroup | null, parentStructureId: number | null): void => {
    const structureId = person.organigramma_structure_id;
    let current: TGroup | null = null;
    let currentStructureId: number | null = null;

    if (structureId !== null && structureId !== undefined && structureById.has(structureId)) {
      const same = parent !== null && structureId === parentStructureId;
      current = same ? parent : ensureGroup(structureId, parent);
      currentStructureId = structureId;
      (current as TGroup).memberIds.push(String(person.id));
    }

    // Lo staff non ha figli disegnati: non si scende oltre
    if (person.is_staff) return;
    visibleChildren(person, collapsed).forEach(child => visit(child, current, currentStructureId));
  };

  tree.forEach(root => visit(root, null, null));
  return groups;
};

const computeGroupBoxes = (
  groups: Map<string, TGroup>,
  positions: Map<string, TCardPosition>,
): Map<string, TRect> => {
  const boxes = new Map<string, TRect>();

  const compute = (groupId: string): TRect | null => {
    if (boxes.has(groupId)) return boxes.get(groupId) as TRect;
    const group = groups.get(groupId) as TGroup;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    group.memberIds.forEach(id => {
      const p = positions.get(id);
      if (!p) return;
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + NODE_WIDTH);
      maxY = Math.max(maxY, p.y + p.height);
    });

    group.childGroupIds.forEach(childId => {
      const child = compute(childId);
      if (!child) return;
      minX = Math.min(minX, child.x);
      minY = Math.min(minY, child.y);
      maxX = Math.max(maxX, child.x + child.width);
      maxY = Math.max(maxY, child.y + child.height);
    });

    if (!isFinite(minX)) return null;

    const box: TRect = {
      x: minX - GROUP_PADDING_X,
      y: minY - GROUP_PADDING_Y - GROUP_HEADER_HEIGHT,
      width: maxX - minX + 2 * GROUP_PADDING_X,
      height: maxY - minY + 2 * GROUP_PADDING_Y + GROUP_HEADER_HEIGHT,
    };
    boxes.set(groupId, box);
    return box;
  };

  groups.forEach((_group, id) => compute(id));
  return boxes;
};

// ---------------------------------------------------------------------------
// API pubblica
// ---------------------------------------------------------------------------

export const rectOfPositions = (
  ids: string[],
  positions: Map<string, TCardPosition>,
): TRect | null => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  ids.forEach(id => {
    const p = positions.get(id);
    if (!p) return;
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + NODE_WIDTH);
    maxY = Math.max(maxY, p.y + p.height);
  });
  if (!isFinite(minX)) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

export const layoutOrganigramma = (
  tree: TOrgPerson[],
  structures: TOrgStructure[],
  collapsed: Set<number>,
  showStructures: boolean,
  /** altezza misurata della card di ogni persona (vedi hooks/useCardHeights) */
  measuredHeights: Map<number, number> = new Map(),
): TOrgLayout => {
  const ctx: TLayoutContext = {
    collapsed,
    metrics: new Map(),
    index: buildOrgIndex(tree),
    heights: assignCardHeights(tree, collapsed, measuredHeights),
    personNodes: [],
    edges: [],
    positions: new Map(),
  };

  // I riquadri si calcolano prima delle posizioni: il layout deve sapere dove si
  // aprono per lasciare spazio a padding ed etichette.
  const structureById = new Map(structures.map(s => [s.id, s] as [number, TOrgStructure]));
  const groups =
    showStructures && structures.length > 0
      ? buildGroups(tree, collapsed, structureById)
      : new Map<string, TGroup>();
  const groupOf: TGroupOf = new Map();
  groups.forEach(group => group.memberIds.forEach(id => groupOf.set(Number(id), group.id)));

  const mctx: TMetricsContext = { collapsed, metrics: ctx.metrics, groupOf, heights: ctx.heights };
  tree.forEach(root => computeMetrics(root, mctx));

  let cursor = 0;
  tree.forEach((root, i) => {
    assignPositions(root, cursor, 0, ctx);
    cursor += (ctx.metrics.get(root.id) as TMetrics).width;
    if (i < tree.length - 1) {
      cursor += siblingGap(root, tree[i + 1], null, ROOT_TREE_GAP, mctx);
    }
  });

  const groupNodes: Node<TGroupNodeData>[] = [];
  const boxes: TRect[] = [];

  if (groups.size > 0) {
    const groupBoxes = computeGroupBoxes(groups, ctx.positions);

    // I gruppi sono in ordine padre -> figlio: z-index crescente con la profondità,
    // ma sempre negativo, così stanno sotto linee e card.
    groups.forEach(group => {
      const box = groupBoxes.get(group.id);
      const structure = structureById.get(group.structureId);
      if (!box || !structure) return;
      boxes.push(box);
      groupNodes.push({
        id: group.id,
        type: GROUP_NODE,
        position: { x: box.x, y: box.y },
        data: {
          structure: {
            id: structure.id,
            name: structure.name,
            background_color: structure.background_color,
            text_color: structure.text_color,
          },
          depth: group.depth,
        },
        style: { width: box.width, height: box.height, pointerEvents: "none" },
        zIndex: -100 + group.depth,
        draggable: false,
        selectable: false,
        connectable: false,
      });
    });
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  ctx.positions.forEach(p => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + NODE_WIDTH);
    maxY = Math.max(maxY, p.y + p.height);
  });
  boxes.forEach(b => {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  });

  const bounds: TRect = isFinite(minX)
    ? { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
    : { x: 0, y: 0, width: 0, height: 0 };

  return {
    nodes: [...groupNodes, ...ctx.personNodes],
    edges: ctx.edges,
    positions: ctx.positions,
    bounds,
  };
};